import { FileMetadata } from "@features/cx/shared/types/consumerRequest";
import { Stepper } from "@shared/components/Stepper";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ComplaintDetailsStep } from "./stepComponents/ComplaintDetailsStep";
import { CustomerInformationStep } from "./stepComponents/CustomerInformationStep";
import { EvidenceAttachmentsStep } from "./stepComponents/EvidenceAttachmentsStep";
import { ReviewSubmitStep } from "./stepComponents/ReviewSubmitStep";
import { useFormCompletionTracker } from "@shared/hooks/useFormCompletionTracker";

// Complaint-specific data structures
interface CustomerInfo {
  accountNumber: string;
  name: string;
  address: string;
  phone: string;
  email: string;
  customerType: string;
  customerSince?: string;
}

interface ComplaintDetails {
  category: string;
  complaintName: string;
  subCategory: string;
  priority: string;
  description: string;
  expectedResolution: string;
  incident: {
    date: string;
  };
}

interface EvidenceData {
  notes: string;
  attachments?: File[];
}

interface ComplaintData {
  customer: CustomerInfo;
  complaint: ComplaintDetails;
  evidence: EvidenceData;
}

// Initial data structures
const initialCustomer: CustomerInfo = {
  accountNumber: "",
  name: "",
  address: "",
  phone: "",
  email: "",
  customerType: "",
  customerSince: "",
};

const initialComplaint: ComplaintDetails = {
  category: "",
  complaintName: "",
  subCategory: "",
  priority: "",
  description: "",
  expectedResolution: "",
  incident: {
    date: "",
  },
};

const initialEvidence: EvidenceData = {
  notes: "",
  attachments: [],
};

const initialComplaintData: ComplaintData = {
  customer: initialCustomer,
  complaint: initialComplaint,
  evidence: initialEvidence,
};

export const CreateComplaintRequest: React.FC = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const params = useParams();
  const storageKey = `complaint-request-progress-${remoteUtilityId}`;

  // Extract consumerId and consumerNo from URL parameters
  const urlConsumerId = params.consumerId ? parseInt(params.consumerId) : null;
  const urlConsumerNo = params.consumerNo || null;

  const { startTracking, logTimeUntilStep, stopTracking } = useFormCompletionTracker("Complaint");

  useEffect(() => {startTracking()}, [startTracking])

  // File state management at parent level
  const [fileObjects, setFileObjects] = useState<File[]>([]);
  const [fileMetadata, setFileMetadata] = useState<FileMetadata[]>([]);
  const [evidenceFormData, setEvidenceFormData] = useState<any>({});
  console.log("fileObjects", fileObjects);

  // Handle file data changes from EvidenceAttachmentsStep
  const handleFileDataChange = (data: any) => {
    if (data.file) {
      console.log("setting fileObjects", data.file);

      setFileObjects(data.file);
    }
    setFileMetadata(data.fileMetadata || []);
    setEvidenceFormData({
      notes: data.notes || "",
      incidentDate: data.incidentDate || "",
      expectedResolution: data.expectedResolution || "",
    });
  };

  // Store the handler on window for child components to access
  React.useEffect(() => {
    (window as any).handleFileDataChange = (data: any) => {
      if (data.file?.length > 0) {
        setFileObjects(data.file);
      }
      setFileMetadata(data.fileMetadata || []);
      setEvidenceFormData({
        notes: data.notes || "",
        incidentDate: data.incidentDate || "",
        expectedResolution: data.expectedResolution || "",
      });
    };

    // Store file data on window for ReviewSubmitStep to access
    (window as any).fileData = {
      fileObjects,
      fileMetadata,
      evidenceFormData,
    };

    return () => {
      delete (window as any).handleFileDataChange;
      delete (window as any).fileData;
    };
  }, [fileObjects, fileMetadata, evidenceFormData]);

  // Stepper steps array
  const steps = [
    {
      title: "Customer Information",
      slug: "customer-info",
      component: (
        <CustomerInformationStep
          remoteUtilityId={Number(remoteUtilityId)}
          storageKey={storageKey}
          logTimeUntilStep={logTimeUntilStep}
          urlConsumerId={urlConsumerId}
          urlConsumerNo={urlConsumerNo}
        />
      ),
      optional: false,
      infoTitle: "Customer Information",
      infoDescription: "Select customer and verify details",
    },
    {
      title: "Complaint Details",
      slug: "complaint-details",
      component: (
        <ComplaintDetailsStep
          remoteUtilityId={Number(remoteUtilityId)}
          storageKey={storageKey}
          logTimeUntilStep={logTimeUntilStep}
        />
      ),
      optional: false,
      infoTitle: "Complaint Details",
      infoDescription: "Specify complaint category and details",
    },
    {
      title: "Evidence & Attachments",
      slug: "evidence-attachments",
      component: (
        <EvidenceAttachmentsStep
          remoteUtilityId={Number(remoteUtilityId)}
          storageKey={storageKey}
          onDataChange={handleFileDataChange}
          logTimeUntilStep={logTimeUntilStep}
        />
      ),
      optional: false,
      infoTitle: "Evidence & Attachments",
      infoDescription: "Upload supporting documents",
    },
    {
      title: "Review & Submit",
      slug: "review-submit",
      component: (
        <ReviewSubmitStep
          remoteUtilityId={Number(remoteUtilityId)}
          storageKey={storageKey}
          fileObjects={fileObjects} // ADD THIS LINE
          fileMetadata={fileMetadata} // ADD THIS LINE
          evidenceFormData={evidenceFormData} // ADD THIS LINE
          logTimeUntilStep={logTimeUntilStep}
          stopTracking={stopTracking}
        />
      ),
      optional: false,
      infoTitle: "Review & Submit",
      infoDescription: "Review and submit the complaint",
    },
  ];

  return (
    <div>
      <h2 className="text-xl font-semibold">Create Complaint Request</h2>
      <Stepper
        steps={steps}
        onStepChange={() => {}}
        onValidation={async () => ({ isValid: true })}
        onComplete={() => {}}
        onStepSave={async (stepIndex, stepData, isDirty) => {
          // if (isDirty) {
          //   await fetch(`/api/complaints/${complaintId}/step/${stepIndex}`, {
          //     method: 'PATCH',
          //     headers: { 'Content-Type': 'application/json' },
          //     body: JSON.stringify(stepData)
          //   });
          // }
        }}
        enableApiSave={true}
        saveOnStepChange={true}
        trackDirtyState={true}
        autoSave={true}
        persistProgress={true}
        allowSkip={true}
        showDraftResume={true}
      />
    </div>
  );
};